/**
 * UnifiedEntitySidebar Component
 * Combines entity management and Table of Contents in a tabbed sidebar
 * Contents tab is default, Entities tab shows all entities with filters
 * Now uses project-scoped backend storage via React Query hooks
 */

import { useState, useMemo, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { List, Sparkles, Check, X, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useSmartDoc,
  useUpdateEntityStatus,
  useAcceptAllEntities,
  useRejectAllEntities,
} from '@/hooks/useSmartDocs'
import { getEntityConfig, type DetectedEntity } from '@/types/document'
import { FACET_TYPE_CONFIG, type FacetCategory } from '@/types/interviewer'
import type { JSONContent } from '@tiptap/core'

// ============================================================================
// Types
// ============================================================================

interface UnifiedEntitySidebarProps {
  documentId: string
  tiptapContent?: JSONContent | null
  onEntityClick?: (entity: DetectedEntity) => void
  onHeadingClick?: (headingText: string) => void
  className?: string
}

type TabType = 'contents' | 'entities'

interface HeadingItem {
  id: string
  text: string
  level: number
}

// ============================================================================
// Entity Domain Mapping
// ============================================================================

const ENTITY_DOMAIN_MAP: Record<string, 'market' | 'business' | 'solution'> = {
  Problem: 'market',
  Person: 'market',
  Stakeholder: 'market',
  problem: 'market',
  current_behavior: 'market',
  workaround: 'market',
  Decision: 'business',
  Risk: 'business',
  Opportunity: 'business',
  goal: 'business',
  strategy: 'business',
  decision: 'business',
  risk: 'business',
  opportunity: 'business',
  success_metric: 'business',
  ActionItem: 'solution',
  Constraint: 'solution',
  feature: 'solution',
  requirement: 'solution',
  action_item: 'solution',
  constraint: 'solution',
}

function getEntityDomain(entity: DetectedEntity): 'market' | 'business' | 'solution' {
  if (entity.suggestedOntologyType && ENTITY_DOMAIN_MAP[entity.suggestedOntologyType]) {
    return ENTITY_DOMAIN_MAP[entity.suggestedOntologyType]
  }
  if (ENTITY_DOMAIN_MAP[entity.type]) {
    return ENTITY_DOMAIN_MAP[entity.type]
  }
  const config = FACET_TYPE_CONFIG[entity.type]
  if (config?.category === 'market') return 'market'
  if (config?.category === 'business') return 'business'
  if (config?.category === 'solution') return 'solution'
  return 'business'
}

// ============================================================================
// Main Component
// ============================================================================

export function UnifiedEntitySidebar({
  documentId,
  tiptapContent,
  onEntityClick,
  onHeadingClick,
  className,
}: UnifiedEntitySidebarProps) {
  const [activeTab, setActiveTab] = useState<TabType>('contents')
  const [filters, setFilters] = useState({
    pending: true,
    market: true,
    business: true,
    solution: true,
  })

  // React Query hooks
  const { data: document, isLoading } = useSmartDoc(documentId)
  const updateEntityStatus = useUpdateEntityStatus()
  const acceptAllEntities = useAcceptAllEntities()
  const rejectAllEntities = useRejectAllEntities()

  const entities = document?.detectedEntities || []

  // Entity counts by domain and status
  const entityCounts = useMemo(() => {
    const counts = {
      total: entities.length,
      pending: 0,
      market: 0,
      business: 0,
      solution: 0,
    }
    entities.forEach((e) => {
      if (e.status === 'pending') counts.pending++
      const domain = getEntityDomain(e)
      counts[domain]++
    })
    return counts
  }, [entities])

  // Filter entities based on active filters
  const filteredEntities = useMemo(() => {
    return entities.filter((e) => {
      // If pending filter is off and entity is pending, hide
      if (!filters.pending && e.status === 'pending') return false
      // Domain filter
      const domain = getEntityDomain(e)
      if (!filters[domain]) return false
      return true
    })
  }, [entities, filters])

  // Extract headings from TipTap content
  const headings = useMemo(() => {
    if (!tiptapContent?.content) return []
    const items: HeadingItem[] = []
    let headingIndex = 0

    const extractHeadings = (nodes: JSONContent[]) => {
      nodes.forEach((node) => {
        if (node.type === 'heading' && node.attrs?.level) {
          const text = node.content
            ?.filter((c) => c.type === 'text')
            .map((c) => c.text)
            .join('') || ''
          if (text.trim()) {
            items.push({
              id: `heading-${headingIndex++}`,
              text: text.trim(),
              level: node.attrs.level,
            })
          }
        }
        if (node.content) {
          extractHeadings(node.content)
        }
      })
    }

    extractHeadings(tiptapContent.content)
    return items
  }, [tiptapContent])

  // Toggle filter
  const toggleFilter = useCallback((key: keyof typeof filters) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // Entity action handlers
  const handleAcceptEntity = useCallback((id: string) => {
    updateEntityStatus.mutate({ docId: documentId, entityId: id, status: 'accepted' })
  }, [documentId, updateEntityStatus])

  const handleRejectEntity = useCallback((id: string) => {
    updateEntityStatus.mutate({ docId: documentId, entityId: id, status: 'rejected' })
  }, [documentId, updateEntityStatus])

  const handleAcceptAll = useCallback(() => {
    acceptAllEntities.mutate(documentId)
  }, [documentId, acceptAllEntities])

  const handleRejectAll = useCallback(() => {
    rejectAllEntities.mutate(documentId)
  }, [documentId, rejectAllEntities])

  // Pending entity count for tab badge
  const pendingCount = entityCounts.pending

  return (
    <div className={cn('h-full flex flex-col bg-warm-100 border-l border-warm-200', className)}>
      {/* Tab Switcher */}
      <div className="flex border-b border-warm-200 bg-white">
        <button
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors border-b-2',
            activeTab === 'contents'
              ? 'text-text-primary border-business'
              : 'text-text-muted border-transparent hover:text-text-secondary'
          )}
          onClick={() => setActiveTab('contents')}
        >
          <List className="w-3.5 h-3.5" />
          Contents
        </button>
        <button
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors border-b-2',
            activeTab === 'entities'
              ? 'text-text-primary border-business'
              : 'text-text-muted border-transparent hover:text-text-secondary'
          )}
          onClick={() => setActiveTab('entities')}
        >
          <Sparkles className="w-3.5 h-3.5" />
          Entities
          {pendingCount > 0 && (
            <span className="bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'contents' ? (
        <ContentsTab headings={headings} onHeadingClick={onHeadingClick} />
      ) : (
        <EntitiesTab
          entities={filteredEntities}
          entityCounts={entityCounts}
          filters={filters}
          onToggleFilter={toggleFilter}
          onAccept={handleAcceptEntity}
          onReject={handleRejectEntity}
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onEntityClick={onEntityClick}
          isDetecting={isLoading}
          isUpdating={updateEntityStatus.isPending || acceptAllEntities.isPending || rejectAllEntities.isPending}
        />
      )}
    </div>
  )
}

// ============================================================================
// Contents Tab
// ============================================================================

interface ContentsTabProps {
  headings: HeadingItem[]
  onHeadingClick?: (headingText: string) => void
}

function ContentsTab({ headings, onHeadingClick }: ContentsTabProps) {
  if (headings.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-text-muted p-6">
        <div className="text-center">
          <List className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No headings found</p>
          <p className="text-xs mt-1">Add headings to see table of contents</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto p-3">
      <ul className="space-y-0.5">
        {headings.map((heading) => (
          <li key={heading.id}>
            <button
              className={cn(
                'w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-colors hover:bg-warm-200',
                heading.level === 1 && 'font-semibold text-text-primary text-[13px]',
                heading.level === 2 && 'pl-5 text-text-secondary',
                heading.level === 3 && 'pl-8 text-text-muted text-[11px]'
              )}
              onClick={() => onHeadingClick?.(heading.text)}
            >
              {heading.text}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ============================================================================
// Entities Tab
// ============================================================================

interface EntitiesTabProps {
  entities: DetectedEntity[]
  entityCounts: {
    total: number
    pending: number
    market: number
    business: number
    solution: number
  }
  filters: {
    pending: boolean
    market: boolean
    business: boolean
    solution: boolean
  }
  onToggleFilter: (key: 'pending' | 'market' | 'business' | 'solution') => void
  onAccept: (id: string) => void
  onReject: (id: string) => void
  onAcceptAll: () => void
  onRejectAll: () => void
  onEntityClick?: (entity: DetectedEntity) => void
  isDetecting: boolean
  isUpdating?: boolean
}

function EntitiesTab({
  entities,
  entityCounts,
  filters,
  onToggleFilter,
  onAccept,
  onReject,
  onAcceptAll,
  onRejectAll,
  onEntityClick,
  isDetecting,
  isUpdating,
}: EntitiesTabProps) {
  const pendingEntities = entities.filter((e) => e.status === 'pending')
  const hasPending = pendingEntities.length > 0

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-warm-200">
        <div className="flex items-center gap-2 text-xs">
          {isDetecting ? (
            <span className="flex items-center gap-1 text-text-muted">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading...
            </span>
          ) : (
            <>
              <strong>{entityCounts.total}</strong>
              <span className="text-text-muted">entities</span>
              {entityCounts.pending > 0 && (
                <span className="bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                  {entityCounts.pending} pending
                </span>
              )}
            </>
          )}
        </div>
        {hasPending && (
          <Button
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={onAcceptAll}
            disabled={isUpdating}
          >
            {isUpdating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Accept All
          </Button>
        )}
      </div>

      {/* Filter Pills */}
      <div className="flex flex-wrap gap-1.5 px-3 py-2 bg-white border-b border-warm-200">
        <FilterPill
          label="Pending"
          count={entityCounts.pending}
          active={filters.pending}
          variant="pending"
          onClick={() => onToggleFilter('pending')}
        />
        <FilterPill
          label="Market"
          count={entityCounts.market}
          active={filters.market}
          variant="market"
          onClick={() => onToggleFilter('market')}
        />
        <FilterPill
          label="Business"
          count={entityCounts.business}
          active={filters.business}
          variant="business"
          onClick={() => onToggleFilter('business')}
        />
        <FilterPill
          label="Solution"
          count={entityCounts.solution}
          active={filters.solution}
          variant="solution"
          onClick={() => onToggleFilter('solution')}
        />
      </div>

      {/* Entity List */}
      <div className="flex-1 overflow-y-auto p-2">
        {entities.length === 0 ? (
          <div className="text-center py-8 text-text-muted">
            <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No entities match filters</p>
          </div>
        ) : (
          <div className="space-y-1">
            {entities.map((entity) => (
              <EntityRow
                key={entity.id}
                entity={entity}
                onAccept={() => onAccept(entity.id)}
                onReject={() => onReject(entity.id)}
                onClick={() => onEntityClick?.(entity)}
                isUpdating={isUpdating}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {hasPending && (
        <div className="px-3 py-2 border-t border-warm-200 bg-white">
          <Button
            variant="outline"
            size="sm"
            className="w-full text-[11px] h-7"
            onClick={onRejectAll}
            disabled={isUpdating}
          >
            {isUpdating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Skip All Pending
          </Button>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Filter Pill
// ============================================================================

interface FilterPillProps {
  label: string
  count: number
  active: boolean
  variant: 'pending' | 'market' | 'business' | 'solution'
  onClick: () => void
}

function FilterPill({ label, count, active, variant, onClick }: FilterPillProps) {
  return (
    <button
      className={cn(
        'flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors',
        active
          ? variant === 'pending'
            ? 'bg-amber-50 text-amber-700 border-amber-300'
            : variant === 'market'
              ? 'bg-market/10 text-market border-market'
              : variant === 'business'
                ? 'bg-business/10 text-business border-business'
                : 'bg-solution/10 text-solution border-solution'
          : 'bg-white text-text-muted border-warm-200'
      )}
      onClick={onClick}
    >
      {label}
      <span
        className={cn(
          'px-1 py-0 rounded text-[9px]',
          active ? 'bg-black/5' : 'bg-warm-100'
        )}
      >
        {count}
      </span>
    </button>
  )
}

// ============================================================================
// Entity Row (Compact)
// ============================================================================

interface EntityRowProps {
  entity: DetectedEntity
  onAccept: () => void
  onReject: () => void
  onClick?: () => void
  isUpdating?: boolean
}

function EntityRow({ entity, onAccept, onReject, onClick, isUpdating }: EntityRowProps) {
  const domain = getEntityDomain(entity)
  const isPending = entity.status === 'pending'
  const config = getEntityConfig(entity.suggestedOntologyType || entity.type)

  return (
    <button
      className={cn(
        'w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left transition-all',
        'bg-white border hover:shadow-sm',
        isPending
          ? 'border-dashed border-warm-300'
          : 'border-solid border-warm-200'
      )}
      onClick={onClick}
    >
      {/* Domain dot */}
      <div
        className={cn(
          'w-1.5 h-1.5 rounded-full flex-shrink-0',
          domain === 'market' && 'bg-market',
          domain === 'business' && 'bg-business',
          domain === 'solution' && 'bg-solution'
        )}
      />

      {/* Label and type */}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-text-primary truncate">
          {entity.content}
        </div>
        <div className="text-[10px] text-text-muted">
          {config.label}
          {isPending && ` • ${Math.round(entity.confidence * 100)}%`}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {isPending ? (
          <>
            <button
              className="w-5 h-5 flex items-center justify-center rounded bg-warm-100 text-text-muted hover:bg-warm-200 transition-colors disabled:opacity-50"
              onClick={onReject}
              title="Skip"
              disabled={isUpdating}
            >
              <X className="w-3 h-3" />
            </button>
            <button
              className="w-5 h-5 flex items-center justify-center rounded bg-business text-white hover:bg-business/90 transition-colors disabled:opacity-50"
              onClick={onAccept}
              title="Accept"
              disabled={isUpdating}
            >
              <Check className="w-3 h-3" />
            </button>
          </>
        ) : (
          <span className="text-solution">
            <Check className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
    </button>
  )
}

export default UnifiedEntitySidebar
